import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Check } from "lucide-react";
import { api } from "@/lib/api";

const TOUR_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

/**
 * `/tickets` — single landing page for all live shows.
 *
 * Three card variants render off the same `/api/shows` response:
 *   • Buy card        →  status==="on_sale" && !private  (Sydney / Adelaide / London)
 *   • Interest card   →  private OR status==="coming_soon" with a date set
 *                        (Melbourne sells privately; Paris not on sale yet)
 *   • Hidden          →  no date and not on sale (Gold Coast / Perth)
 */
export default function Tickets() {
  const [shows, setShows] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shows");
        if (alive) setShows(data.shows || []);
      } catch {
        if (alive) setShows([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onSaleShows = (shows || []).filter((s) => s.status === "on_sale" && !s.private);
  const interestShows = (shows || []).filter(
    (s) => s.label && (s.private || (s.status === "coming_soon" && s.date))
  );

  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="tickets-page">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14 sm:py-20">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3 text-center">
          By George Maple
        </p>
        <h1
          className="deco-text text-5xl sm:text-7xl uppercase text-center leading-[0.95] mb-3"
          style={{ letterSpacing: "0.1em" }}
        >
          Metamorph
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/45 text-center mb-12 sm:mb-16">
          Select your show
        </p>

        {shows === null ? (
          <p className="text-center font-mono text-[11px] uppercase tracking-[0.3em] text-white/55">Loading shows…</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shows-grid">
              {onSaleShows.map((s) => (
                <BuyCard key={s.city} s={s} />
              ))}
            </div>

            {interestShows.length > 0 && (
              <>
                <div className="text-center mt-16 mb-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/45">
                    More shows
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="interest-grid">
                  {interestShows.filter((s) => s.label).map((s) => (
                    s.private
                      ? <ComingSoonCard key={s.city} s={s} />
                      : <InterestCard key={s.city} s={s} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        <p className="text-center mt-14 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/" className="hover:text-white/85">← Back to Metamorph</Link>
        </p>
      </div>
    </main>
  );
}

function BuyCard({ s }) {
  const soldOut = s.remaining <= 0;
  // Whole card (image + body) is a single Link to checkout so on mobile
  // the entire card surface is tappable, not just the small "Tickets →" text.
  return (
    <Link
      to={`/buy/${s.buy_slug}`}
      className="border border-white/15 hover:border-white/35 transition group flex flex-col"
      data-testid={`show-card-${s.city}`}
      aria-label={`Buy ${s.label} tickets`}
    >
      <div
        className="aspect-[5/4] bg-cover bg-center border-b border-white/10 grayscale-[10%] group-hover:grayscale-0 transition"
        style={{ backgroundImage: `url(${TOUR_HERO_IMAGE})` }}
      />
      <div className="p-5 flex-1 flex flex-col">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1">On sale</p>
        <h2
          className="text-2xl mb-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
        >
          {s.label}
        </h2>
        {s.rescheduled && (
          <span className="font-mono text-[9.5px] uppercase tracking-[0.32em] text-[#f3e0a6] border border-[#f3e0a6]/40 px-1.5 py-0.5 self-start mb-2">** New date</span>
        )}
        {s.date && (
          <p className="flex items-center gap-1.5 text-[12px] text-white/70 mb-1">
            <Calendar className="w-3 h-3" strokeWidth={1.4} /> {s.date}
          </p>
        )}
        {s.venue ? (
          <p className="flex items-center gap-1.5 text-[12px] text-white/55 mb-4">
            <MapPin className="w-3 h-3" strokeWidth={1.4} /> {s.venue}
          </p>
        ) : <div className="mb-4" />}

        <div className="mt-auto pt-3 border-t border-white/10 flex items-center justify-between">
          <span
            className="tabular-nums"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", fontSize: "20px", fontWeight: 500 }}
          >
            {s.currency_symbol}{Math.round(s.price)}
          </span>
          {!soldOut ? (
            <span
              className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.28em] text-white/85 group-hover:text-[var(--mm-accent)] transition"
              data-testid={`buy-${s.city}`}
            >
              Tickets <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.4} />
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-rose-300/80">Sold out</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function ComingSoonCard({ s }) {
  // Quiet "Coming soon" placeholder — no form, no buy CTA. Used for
  // private-invite cities (e.g. Melbourne) where we want to occupy the
  // grid slot without inviting public sign-ups.
  return (
    <div
      className="border border-white/15 flex flex-col"
      data-testid={`coming-soon-card-${s.city}`}
    >
      <div
        className="aspect-[5/4] bg-cover bg-center border-b border-white/10 opacity-60"
        style={{ backgroundImage: `url(${TOUR_HERO_IMAGE})` }}
      />
      <div className="p-5 flex-1 flex flex-col items-center justify-center text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-2">Coming soon</p>
        <h2
          className="text-2xl"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
        >
          {s.label}
        </h2>
      </div>
    </div>
  );
}

function InterestCard({ s }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/tickets/interest", {
        city: s.city,
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <div
      className="border border-white/15 hover:border-white/35 transition flex flex-col"
      data-testid={`interest-card-${s.city}`}
    >
      <div
        className="aspect-[5/4] bg-cover bg-center border-b border-white/10 opacity-70"
        style={{ backgroundImage: `url(${TOUR_HERO_IMAGE})` }}
      />
      <div className="p-5 flex-1 flex flex-col">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1">Register your interest</p>
        <h2
          className="text-2xl mb-3"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
        >
          {s.label}
        </h2>

        {status === "done" ? (
          <div className="flex flex-col items-center gap-2 py-4" data-testid={`interest-${s.city}-done`}>
            <Check className="w-5 h-5 text-[var(--mm-accent-hover)]" strokeWidth={1.4} />
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">Noted — we&rsquo;ll be in touch.</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-2.5" data-testid={`interest-${s.city}-form`}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-white font-mono tracking-wide placeholder-white/45"
              data-testid={`interest-${s.city}-name`}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-white font-mono tracking-wide placeholder-white/45"
              data-testid={`interest-${s.city}-email`}
              required
            />
            <button
              type="submit"
              disabled={status === "submitting" || !email.trim()}
              className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed mt-1 text-[11px]"
              data-testid={`interest-${s.city}-submit`}
            >
              {status === "submitting" ? "Sending…" : "Notify me"}
            </button>
            {status === "error" && <p className="font-mono text-[10px] text-red-400/75 text-center mt-1">Something went wrong — try again.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
